/**
 * R2 Storage Analytics Page
 *
 * Cloudflare R2 file storage analytics including
 * total storage, file counts, tier breakdown, content types, and trends.
 */

import { useState, useEffect } from 'react';
import { thirdPartyAnalyticsAPI } from '../services/api';
import { useEnvironment } from '../context/EnvironmentContext';
import PageHeader from '../components/analytics/PageHeader';
import PeriodSelector from '../components/analytics/PeriodSelector';
import MetricCard from '../components/analytics/MetricCard';
import TrendChart from '../components/founder-dashboard/TrendChart';
import DataTable from '../components/founder-dashboard/DataTable';
import DistributionChart from '../components/founder-dashboard/DistributionChart';
import {
  CloudIcon,
  DocumentIcon,
  CalculatorIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

export default function R2StoragePage() {
  const { currentEnv } = useEnvironment();
  const [overview, setOverview] = useState(null);
  const [byTier, setByTier] = useState(null);
  const [byOrganization, setByOrganization] = useState(null);
  const [byContentType, setByContentType] = useState(null);
  const [trends, setTrends] = useState(null);
  const [period, setPeriod] = useState('30d');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [overviewRes, tierRes, orgsRes, contentRes, trendsRes] = await Promise.all([
        thirdPartyAnalyticsAPI.getR2Overview(),
        thirdPartyAnalyticsAPI.getR2ByTier(),
        thirdPartyAnalyticsAPI.getR2ByOrganization(),
        thirdPartyAnalyticsAPI.getR2ByContentType(),
        thirdPartyAnalyticsAPI.getR2Trends(period),
      ]);
      setOverview(overviewRes.data);
      setByTier(tierRes.data);
      setByOrganization(orgsRes.data);
      setByContentType(contentRes.data);
      setTrends(trendsRes.data);
    } catch (err) {
      console.error('[R2StoragePage] Error fetching data:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentEnv, period]);

  // Get cost status
  const getCostStatus = () => {
    const cost = overview?.estimatedMonthlyCost || 0;
    if (cost > 25) return 'critical';
    if (cost > 10) return 'warning';
    return 'healthy';
  };

  // Format bytes to human readable
  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(2)} GB`;
    if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(2)} MB`;
    if (bytes >= 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${bytes} B`;
  };

  if (error) {
    return (
      <div className="p-6">
        <PageHeader title="R2 Storage Analytics" />
        <div className="mt-6 bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-800 font-medium">Error loading R2 storage analytics</p>
          <p className="text-red-600 text-sm mt-1">{error}</p>
          <button
            onClick={fetchData}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Storage by tier distribution - backend returns tierName and totalStorageBytes
  const tierDistribution = {};
  (byTier?.tiers || []).forEach((t) => {
    tierDistribution[t.tierName || t.tierCode] = t.totalStorageBytes;
  });

  // Content type distribution - backend returns contentTypes with totalSize
  const contentDistribution = {};
  const totalContentSize = (byContentType?.contentTypes || []).reduce((sum, t) => sum + (t.totalSize || 0), 0);
  (byContentType?.contentTypes || []).forEach((t) => {
    const pct = totalContentSize > 0 ? ((t.totalSize || 0) / totalContentSize) * 100 : 0;
    contentDistribution[t.friendlyName || t.contentType] = pct;
  });

  // Organization table columns - backend returns camelCase
  const orgColumns = [
    { key: 'organizationName', header: 'Organization', sortable: true },
    {
      key: 'storageUsedBytes',
      header: 'Storage',
      sortable: true,
      render: (val) => formatBytes(val),
    },
    { key: 'attachmentCount', header: 'Files', sortable: true },
    {
      key: 'usagePercentage',
      header: '% of Limit',
      sortable: true,
      render: (val, row) => {
        const pct = val || 0;
        const isHigh = pct > 80;
        return (
          <span className={isHigh ? 'text-orange-600 font-medium' : ''}>
            {isHigh && '\u26A0\uFE0F '}{pct.toFixed(1)}%
          </span>
        );
      },
    },
  ];

  // Organizations near limit - backend returns orgsNearLimit directly, or filter topOrganizations
  const orgsNearLimit = byOrganization?.orgsNearLimit ||
    (byOrganization?.topOrganizations || []).filter((org) => org.usagePercentage > 80);

  // Upload trends - backend returns 'data' array with cumulativeBytes
  const uploadTrendData = (trends?.data || []).map(t => ({
    date: t.date,
    uploads: t.uploads,
    cumulativeGB: t.cumulativeBytes / (1024 * 1024 * 1024),
  }));
  const uploadTrendLines = [
    { key: 'uploads', name: 'Uploads', color: '#3B82F6' },
    { key: 'cumulativeGB', name: 'Cumulative (GB)', color: '#22C55E' },
  ];

  return (
    <div className="p-6 space-y-8">
      {/* Header */}
      <PageHeader
        title="R2 Storage Analytics"
        description="File storage usage and cost metrics"
        onRefresh={fetchData}
        loading={loading}
      >
        <PeriodSelector value={period} onChange={setPeriod} />
      </PageHeader>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Storage"
          value={overview?.totalStorageGB}
          format="gb"
          icon={CloudIcon}
          loading={loading}
        />
        <MetricCard
          title="Total Files"
          value={overview?.totalAttachments}
          icon={DocumentIcon}
          loading={loading}
        />
        <MetricCard
          title="Avg File Size"
          value={overview?.avgFileSize ? Number((overview.avgFileSize / (1024 * 1024)).toFixed(2)) : null}
          subtitle="MB"
          icon={CalculatorIcon}
          loading={loading}
        />
        <MetricCard
          title="Est. Monthly Cost"
          value={overview?.estimatedMonthlyCost}
          format="currency"
          icon={CurrencyDollarIcon}
          status={getCostStatus()}
          loading={loading}
        />
      </div>

      {/* Storage by Tier + Content Type */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* By Tier */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Storage by Tier
          </h3>
          {loading ? (
            <div className="h-48 bg-gray-50 rounded-lg animate-pulse"></div>
          ) : (
            <DistributionChart
              data={tierDistribution}
              height={200}
              showPercentage
            />
          )}
        </div>

        {/* By Content Type */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Storage by Content Type
          </h3>
          {loading ? (
            <div className="h-48 bg-gray-50 rounded-lg animate-pulse"></div>
          ) : (
            <DistributionChart
              data={contentDistribution}
              height={200}
              showPercentage
            />
          )}
        </div>
      </div>

      {/* Organizations Near Limit Warning */}
      {orgsNearLimit.length > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <ExclamationTriangleIcon className="w-5 h-5 text-orange-600" />
            <h3 className="text-lg font-semibold text-orange-800">
              {orgsNearLimit.length} Organization{orgsNearLimit.length > 1 ? 's' : ''} Near Storage Limit
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-orange-700 border-b border-orange-200">
                  <th className="pb-2">Organization</th>
                  <th className="pb-2 text-right">Tier</th>
                  <th className="pb-2 text-right">Used</th>
                  <th className="pb-2 text-right">Limit</th>
                  <th className="pb-2 text-right">% Used</th>
                </tr>
              </thead>
              <tbody>
                {orgsNearLimit.map((org) => (
                  <tr key={org.organization_id} className="border-b border-orange-100">
                    <td className="py-2 font-medium">{org.organization_name}</td>
                    <td className="py-2 text-right capitalize">{org.tier}</td>
                    <td className="py-2 text-right">{formatBytes(org.storage_bytes)}</td>
                    <td className="py-2 text-right">{formatBytes(org.storage_limit)}</td>
                    <td className="py-2 text-right font-medium text-orange-700">
                      {org.percentage_of_limit.toFixed(1)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Top Organizations by Usage */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Top 20 Organizations by Usage
        </h3>
        <DataTable
          data={(byOrganization?.topOrganizations || []).slice(0, 20)}
          columns={orgColumns}
          loading={loading}
          emptyMessage="No organization data available"
        />
      </div>

      {/* Upload Trends */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Upload Trends ({period})
        </h3>
        {loading ? (
          <div className="h-64 bg-gray-50 rounded-lg animate-pulse"></div>
        ) : (
          <TrendChart
            data={uploadTrendData}
            lines={uploadTrendLines}
            height={250}
            xAxisKey="date"
            type="line"
          />
        )}
      </div>
    </div>
  );
}
